import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorDeApi, peticion } from '../api/cliente.js';
import type { DatosDeSesion, Entidad, RespuestaDeIdentificacion } from '../api/tipos.js';
import { Aviso, BotonAtras, Campo } from '../componentes/comunes.js';
import { nombreDeCompania } from '../nombres.js';
import { textos } from '../textos.js';

/**
 * Paso 2: identificarse (§4, §9).
 *
 * El NIF y el número de cliente identifican; lo que acredita es el código que
 * llega al correo de la ficha, y solo la primera vez desde cada equipo.
 */

type Fase =
  | { tipo: 'datos' }
  // `pistaCorreo` es `null` cuando el código viene de un agente por teléfono
  // (D-67), no de un correo: es lo que decide qué ayuda enseñar debajo del
  // título, sin necesitar una fase aparte para algo que en todo lo demás es
  // la misma pantalla.
  | { tipo: 'codigo'; solicitudId: string; pistaCorreo: string | null }
  | { tipo: 'sin_soporte'; mensaje: string; urlCompra: string | null }
  | { tipo: 'sin_correo'; mensaje: string };

export function PantallaAcceso({
  entidad,
  alEntrar,
  alVolver,
}: {
  entidad: Entidad;
  alEntrar: (sesion: DatosDeSesion) => void;
  alVolver: () => void;
}) {
  const [nif, setNif] = useState('');
  const [numeroCliente, setNumeroCliente] = useState('');
  const [codigo, setCodigo] = useState('');
  const [fase, setFase] = useState<Fase>({ tipo: 'datos' });
  const [error, setError] = useState<string | null>(null);

  const identificar = useMutation({
    mutationFn: () =>
      peticion<RespuestaDeIdentificacion>('/auth/identificar', {
        metodo: 'POST',
        cuerpo: { entidad: entidad.codigo, nif, numeroCliente },
      }),
    onSuccess: (respuesta) => {
      setError(null);
      switch (respuesta.estado) {
        case 'sesion':
          alEntrar(respuesta);
          return;
        case 'codigo_enviado':
          setFase({
            tipo: 'codigo',
            solicitudId: respuesta.solicitudId,
            pistaCorreo: respuesta.pistaCorreo,
          });
          return;
        case 'sin_soporte':
          setFase({
            tipo: 'sin_soporte',
            mensaje: respuesta.mensaje,
            urlCompra: respuesta.urlCompra,
          });
          return;
        case 'sin_correo':
          setFase({ tipo: 'sin_correo', mensaje: respuesta.mensaje });
          return;
        case 'codigo_por_telefono':
          setFase({ tipo: 'codigo', solicitudId: respuesta.solicitudId, pistaCorreo: null });
          return;
      }
    },
    onError: (fallo: unknown) => {
      setError(fallo instanceof ErrorDeApi ? fallo.message : textos.errores.generico);
    },
  });

  const verificar = useMutation({
    mutationFn: (solicitudId: string) =>
      peticion<{ estado: 'sesion' } & DatosDeSesion>('/auth/verificar', {
        metodo: 'POST',
        cuerpo: { solicitudId, codigo },
      }),
    onSuccess: (respuesta) => {
      setError(null);
      alEntrar(respuesta);
    },
    onError: (fallo: unknown) => {
      setError(fallo instanceof ErrorDeApi ? fallo.message : textos.errores.generico);
    },
  });

  function empezarDeNuevo() {
    setFase({ tipo: 'datos' });
    setCodigo('');
    setError(null);
  }

  if (fase.tipo === 'sin_soporte') {
    return (
      <section className="pantalla-acceso" aria-labelledby="titulo-acceso">
        <BotonAtras alClick={empezarDeNuevo} />
        <h2 id="titulo-acceso">{textos.acceso.sinSoporteTitulo}</h2>
        <Aviso tipo="atencion">{fase.mensaje}</Aviso>
        <div className="botonera">
          {fase.urlCompra !== null && (
            <a
              className="principal"
              style={{ padding: '0.6rem 1.1rem', borderRadius: 10 }}
              href={fase.urlCompra}
            >
              {textos.acceso.contratar}
            </a>
          )}
          <button type="button" className="enlace" onClick={empezarDeNuevo}>
            {textos.aplicacion.volver}
          </button>
        </div>
      </section>
    );
  }

  if (fase.tipo === 'sin_correo') {
    return (
      <section className="pantalla-acceso" aria-labelledby="titulo-acceso">
        <BotonAtras alClick={empezarDeNuevo} />
        <h2 id="titulo-acceso">{textos.acceso.sinCorreoTitulo}</h2>
        <Aviso tipo="atencion">{fase.mensaje}</Aviso>
        <button type="button" className="enlace" onClick={empezarDeNuevo}>
          {textos.aplicacion.volver}
        </button>
      </section>
    );
  }

  if (fase.tipo === 'codigo') {
    const { pistaCorreo, solicitudId } = fase;
    // `pistaCorreo === null`: un agente autorizó el acceso por teléfono
    // (D-60, D-67). Es la misma pantalla — mismo campo, mismo botón—, con
    // otro título y otra ayuda porque no hay ningún correo que revisar.
    return (
      <section className="pantalla-acceso pantalla-codigo" aria-labelledby="titulo-codigo">
        <BotonAtras alClick={empezarDeNuevo} />
        <h2 id="titulo-codigo">
          {pistaCorreo === null ? textos.acceso.codigoTituloTelefono : textos.acceso.codigoTitulo}
        </h2>
        <p className="ayuda">
          {pistaCorreo === null
            ? textos.acceso.codigoAyudaTelefono
            : textos.acceso.codigoAyuda(pistaCorreo)}
        </p>

        {error !== null && <Aviso tipo="error">{error}</Aviso>}

        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            verificar.mutate(solicitudId);
          }}
        >
          <Campo
            id="codigo"
            etiqueta={textos.acceso.codigo}
            pista={
              pistaCorreo === null ? textos.acceso.codigoNotaTelefono : textos.acceso.codigoNota
            }
          >
            {(props) => (
              <input
                {...props}
                value={codigo}
                onChange={(evento) => setCodigo(evento.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                autoFocus
              />
            )}
          </Campo>

          <div className="botonera">
            <button
              type="submit"
              className="principal"
              disabled={verificar.isPending || codigo.length !== 6}
            >
              {textos.acceso.verificar}
            </button>
            <button type="button" className="enlace" onClick={empezarDeNuevo}>
              {textos.acceso.otroCodigo}
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section className="pantalla-acceso" aria-labelledby="titulo-acceso">
      <BotonAtras alClick={alVolver} />
      <h2 id="titulo-acceso">{textos.acceso.titulo}</h2>
      <p className="ayuda">
        {nombreDeCompania(entidad)}. {textos.acceso.ayuda}
      </p>

      {error !== null && <Aviso tipo="error">{error}</Aviso>}

      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          identificar.mutate();
        }}
      >
        <Campo id="nif" etiqueta={textos.acceso.nif} pista={textos.acceso.nifAyuda}>
          {(props) => (
            <input
              {...props}
              value={nif}
              onChange={(evento) => setNif(evento.target.value)}
              autoComplete="off"
              spellCheck={false}
              required
              minLength={4}
              autoFocus
            />
          )}
        </Campo>

        <Campo
          id="numero-cliente"
          etiqueta={textos.acceso.numeroCliente}
          pista={textos.acceso.numeroClienteAyuda}
        >
          {(props) => (
            <input
              {...props}
              value={numeroCliente}
              onChange={(evento) => setNumeroCliente(evento.target.value)}
              autoComplete="off"
              spellCheck={false}
              required
            />
          )}
        </Campo>

        <div className="botonera">
          <button type="submit" className="principal" disabled={identificar.isPending}>
            {identificar.isPending ? textos.aplicacion.cargando : textos.acceso.entrar}
          </button>
          <button type="button" className="enlace" onClick={alVolver}>
            {textos.aplicacion.volver}
          </button>
        </div>
      </form>
    </section>
  );
}
